'use client'

/*
 * MentalRotationTest
 *
 * Two shapes side by side. Is the right one the same shape turned, or its
 * mirror image? The classic Shepard and Metzler (1971) paradigm, in 2D.
 *
 * WHAT GETS SCORED. Rotation speed, in degrees per second, and it comes from
 * the SLOPE of answer time against rotation angle rather than from total time.
 * The robust finding behind the task is that time rises in a straight line with
 * the angle between the shapes, as though the image were being turned. The
 * slope of that line is the rotation. The intercept is everything else: seeing
 * the shapes, deciding, pressing a key, and whatever lag the device adds. Using
 * the slope means none of that leaks into the score, the same reason the Stroop
 * test scores a difference.
 *
 * WHAT COUNTS AS A USABLE RESULT. Same or mirror is a coin flip, so chance is
 * 50%. Below 75% correct the result is withheld. Only correct answers go into
 * the slope, and answers under 300 ms or over 7.5 s are dropped as slips and
 * lapses.
 *
 * ABOUT THE COMPARISON. There is no normative sample for this format, and the
 * best-known reference figure, roughly 60 degrees per second, comes from 3D
 * block figures, which are slower to turn than flat shapes. So this tool shows
 * descriptive labels and that reference point, and no "better than N%" at all.
 * Inventing a distribution to hang one on would be exactly the kind of number
 * the rest of the site refuses to show.
 *
 * SHAPES. Every shape below was checked to be chiral (its mirror image cannot be
 * reached by rotation, or "mirror" would sometimes be a wrong answer marked
 * right) and to have no 180 degree symmetry (or a half turn would look like no
 * turn at all and flatten the slope). Check any new shape the same way.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import Link from 'next/link'
import { Rotate3d, RotateCcw, ArrowRight } from 'lucide-react'
import TestPromptPopup from '@/components/TestPromptPopup'

const SHAPES = [
  [[0, 0], [1, 0], [1, 1], [1, 2], [2, 2], [1, 3]],
  [[0, 0], [0, 1], [1, 1], [2, 1], [2, 2], [3, 2]],
  [[0, 0], [1, 0], [2, 0], [2, 1], [3, 1], [1, 2]],
  [[1, 0], [1, 1], [0, 1], [1, 2], [2, 2], [2, 3], [3, 3]],
  [[0, 0], [0, 1], [0, 2], [1, 2], [2, 2], [2, 3], [1, 0]],
  [[0, 1], [1, 1], [1, 0], [2, 1], [2, 2], [2, 3], [3, 3]],
  [[0, 0], [1, 0], [1, 1], [2, 1], [2, 2], [2, 3], [3, 2]],
  [[0, 3], [0, 2], [1, 2], [1, 1], [2, 1], [2, 0], [3, 0], [1, 3]],
]

const ANGLES = [0, 45, 90, 135, 180]
const REPS_PER_CELL = 4 // per angle, per same/mirror
const SCORED_TOTAL = ANGLES.length * 2 * REPS_PER_CELL
const PRACTICE_TOTAL = 4
const FIXATION_MS = 400
const ERROR_FLASH_MS = 700
const MIN_RT = 300
const MAX_RT = 7500
const MIN_ACCURACY = 0.75
// Below this many ms per degree the slope is too shallow to separate from
// noise, which would mean an absurd rotation speed.
const MIN_SLOPE = 0.2

// Descriptive labels, not a ranking. See the note at the top of the file for
// why there is no population comparison.
const TIERS = [
  { min: 500, label: 'Instant rotator', sub: 'Turning shapes about as fast as you see them', ring: 'from-fuchsia-500 to-purple-600' },
  { min: 300, label: 'Quick turner', sub: 'Fast, clean rotations', ring: 'from-cyan-500 to-blue-600' },
  { min: 180, label: 'Smooth', sub: 'A steady, confident turn', ring: 'from-emerald-500 to-teal-600' },
  { min: 100, label: 'Steady', sub: 'Careful and methodical', ring: 'from-indigo-500 to-violet-600' },
  { min: 0, label: 'Deliberate', sub: 'Slow and sure, which is the right order', ring: 'from-amber-500 to-orange-600' },
]
const tierFor = (rate) => TIERS.find((t) => rate >= t.min)

const randomInt = (n) => Math.floor(Math.random() * n)

function shuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = randomInt(i + 1)
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function makeTrial({ angle, same, practice }, previousShape) {
  let shape
  do { shape = randomInt(SHAPES.length) } while (shape === previousShape)
  // Both the starting orientation and which side is mirrored are random, so the
  // shapes cannot be learned in one canonical pose over the run.
  const base = randomInt(8) * 45
  const direction = Math.random() < 0.5 ? 1 : -1
  const leftMirrored = Math.random() < 0.5
  return {
    shape,
    angle,
    same,
    practice,
    left: { rotation: base, mirrored: leftMirrored },
    right: { rotation: base + direction * angle, mirrored: same ? leftMirrored : !leftMirrored },
  }
}

function buildTrials() {
  const scored = []
  for (const angle of ANGLES) {
    for (const same of [true, false]) {
      for (let r = 0; r < REPS_PER_CELL; r++) scored.push({ angle, same, practice: false })
    }
  }
  const practice = shuffle([
    { angle: 0, same: true },
    { angle: 90, same: false },
    { angle: 45, same: true },
    { angle: 135, same: false },
  ]).map((t) => ({ ...t, practice: true }))

  const trials = []
  for (const spec of [...practice, ...shuffle(scored)]) {
    trials.push(makeTrial(spec, trials[trials.length - 1]?.shape))
  }
  return trials
}

const mean = (xs) => xs.reduce((sum, x) => sum + x, 0) / xs.length

function scoreRun(results) {
  const scored = results.filter((r) => !r.practice)
  const correct = scored.filter((r) => r.correct)
  const timed = correct.filter((r) => r.rt >= MIN_RT && r.rt <= MAX_RT)
  const accuracy = correct.length / scored.length
  const base = { accuracy, correct: correct.length, total: scored.length }

  if (accuracy < MIN_ACCURACY || timed.length < 20) return { ...base, usable: false }

  // Least-squares line of answer time on angle.
  const mx = mean(timed.map((r) => r.angle))
  const my = mean(timed.map((r) => r.rt))
  let num = 0
  let den = 0
  for (const r of timed) {
    num += (r.angle - mx) * (r.rt - my)
    den += (r.angle - mx) ** 2
  }
  const slope = den > 0 ? num / den : 0

  const byAngle = ANGLES.map((angle) => {
    const rts = timed.filter((r) => r.angle === angle).map((r) => r.rt)
    return { angle, ms: rts.length ? Math.round(mean(rts)) : null }
  })

  return {
    ...base,
    usable: true,
    rate: slope >= MIN_SLOPE ? Math.round(1000 / slope) : null,
    byAngle,
  }
}

function Shape({ cells, rotation, mirrored }) {
  const w = Math.max(...cells.map(([x]) => x)) + 1
  const h = Math.max(...cells.map(([, y]) => y)) + 1
  return (
    <svg viewBox="-3 -3 6 6" className="w-full h-full" aria-hidden="true">
      <g transform={`rotate(${rotation}) scale(${mirrored ? -1 : 1} 1)`}>
        {cells.map(([x, y]) => (
          <rect
            key={`${x}-${y}`}
            x={x - w / 2 + 0.04}
            y={y - h / 2 + 0.04}
            width={0.92}
            height={0.92}
            rx={0.12}
            fill="white"
          />
        ))}
      </g>
    </svg>
  )
}

export default function MentalRotationTest() {
  const [phase, setPhase] = useState('idle') // idle | running | finished
  const [stage, setStage] = useState('fixation') // fixation | stimulus | error
  const [index, setIndex] = useState(0)
  const [result, setResult] = useState(null)
  const [best, setBest] = useState(null)
  const [isNewBest, setIsNewBest] = useState(false)
  const [countUp, setCountUp] = useState(0)

  // Mirrored in refs because answers arrive from a window keydown listener and
  // chained timeouts that would otherwise read stale state.
  const trials = useRef([])
  const results = useRef([])
  const indexRef = useRef(0)
  const stageRef = useRef('fixation')
  const shownAt = useRef(0)
  const bestRef = useRef(null)
  const rootRef = useRef(null)

  const timers = useRef([])
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = [] }
  useEffect(() => clearTimers, [])

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem('biqt_rotation_best')
      const parsed = stored === null ? null : parseInt(stored, 10)
      bestRef.current = Number.isFinite(parsed) ? parsed : null
      setBest(bestRef.current)
    } catch { setBest(null) }
  }, [])

  const setStageBoth = (s) => { stageRef.current = s; setStage(s) }

  const finish = useCallback(() => {
    clearTimers()
    const scored = scoreRun(results.current)
    setResult(scored)
    setPhase('finished')

    if (scored.usable && scored.rate !== null && (bestRef.current === null || scored.rate > bestRef.current)) {
      setIsNewBest(bestRef.current !== null)
      bestRef.current = scored.rate
      setBest(scored.rate)
      try { window.localStorage.setItem('biqt_rotation_best', String(scored.rate)) } catch {}
    }
  }, [])

  const runTrial = useCallback((i) => {
    clearTimers()
    indexRef.current = i
    setIndex(i)
    setStageBoth('fixation')
    timers.current.push(setTimeout(() => {
      shownAt.current = performance.now()
      setStageBoth('stimulus')
    }, FIXATION_MS))
  }, [])

  const respond = useCallback((saidSame) => {
    if (stageRef.current !== 'stimulus') return
    const rt = performance.now() - shownAt.current
    const trial = trials.current[indexRef.current]
    const correct = saidSame === trial.same
    results.current.push({ practice: trial.practice, angle: trial.angle, same: trial.same, rt, correct })

    const next = indexRef.current + 1
    const advance = () => (next >= trials.current.length ? finish() : runTrial(next))

    if (correct) {
      advance()
    } else {
      setStageBoth('error')
      timers.current.push(setTimeout(advance, ERROR_FLASH_MS))
    }
  }, [finish, runTrial])

  useEffect(() => {
    if (phase !== 'running') return
    const onKey = (e) => {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return
      const key = e.key.toLowerCase()
      if (key === 's' || key === 'arrowleft') { e.preventDefault(); respond(true) }
      if (key === 'm' || key === 'arrowright') { e.preventDefault(); respond(false) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase, respond])

  const start = () => {
    trials.current = buildTrials()
    results.current = []
    setResult(null)
    setIsNewBest(false)
    setPhase('running')
    runTrial(0)
    // Keep both answer buttons on screen on a phone. Scrolled by hand because
    // scrollIntoView parks the top of the board under the sticky nav.
    const top = rootRef.current?.getBoundingClientRect().top
    if (top !== undefined) window.scrollTo({ top: window.scrollY + top - 80, behavior: 'smooth' })
  }

  const headline = result?.usable ? result.rate ?? Math.round(result.accuracy * 100) : 0
  useEffect(() => {
    if (phase !== 'finished' || !result?.usable) { setCountUp(0); return }
    let frame
    const started = performance.now()
    const tick = (now) => {
      const t = Math.min(1, (now - started) / 650)
      setCountUp(Math.round((1 - Math.pow(1 - t, 3)) * headline))
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [phase, result, headline])

  const trial = trials.current[index]
  const isPractice = trial?.practice
  const scoredNumber = index - PRACTICE_TOTAL + 1
  const hasRate = result?.usable && result.rate !== null
  const tier = hasRate ? tierFor(result.rate) : null
  const slowest = result?.usable ? Math.max(...result.byAngle.map((b) => b.ms ?? 0)) : 0

  return (
    <div ref={rootRef} className="bg-white border border-gray-200 rounded-3xl shadow-xl overflow-hidden">
      {phase !== 'finished' ? (
        <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 px-6 sm:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs font-semibold text-blue-200 uppercase tracking-wide">
                {phase === 'running' && isPractice ? 'Practice' : 'Round'}
              </p>
              <p className="text-3xl font-bold text-white tabular-nums leading-none">
                {phase !== 'running'
                  ? `0/${SCORED_TOTAL}`
                  : isPractice
                    ? `${index + 1}/${PRACTICE_TOTAL}`
                    : `${scoredNumber}/${SCORED_TOTAL}`}
              </p>
            </div>
            <p className="text-sm font-semibold text-white/90 text-right">
              {phase === 'idle' ? 'Ready when you are' : 'Same shape, or a mirror image?'}
            </p>
          </div>

          <div
            className={`relative grid grid-cols-2 gap-3 sm:gap-4 mx-auto w-full max-w-md rounded-2xl ring-4 transition-colors ${
              phase === 'running' && stage === 'error' ? 'ring-rose-400' : 'ring-transparent'
            }`}
          >
            {[0, 1].map((side) => {
              const showing = phase === 'idle' || (phase === 'running' && stage !== 'fixation')
              const demo = { rotation: side ? 90 : 0, mirrored: false }
              const pose = phase === 'idle' ? demo : trial?.[side ? 'right' : 'left']
              return (
                <div key={side} className="aspect-square rounded-2xl bg-white/10 p-3 sm:p-4 flex items-center justify-center">
                  {showing && pose ? (
                    <Shape cells={SHAPES[phase === 'idle' ? 0 : trial.shape]} rotation={pose.rotation} mirrored={pose.mirrored} />
                  ) : (
                    <span className="text-3xl font-light text-white/40">+</span>
                  )}
                </div>
              )
            })}
            {phase === 'running' && stage === 'error' && trial && (
              <span className="absolute -bottom-7 left-0 right-0 text-center text-sm font-semibold text-rose-200">
                {trial.same ? 'It was the same shape' : 'It was a mirror image'}
              </span>
            )}
          </div>

          {phase === 'idle' ? (
            <div className="text-center mt-7">
              <button
                type="button"
                onClick={start}
                className="inline-flex items-center bg-white text-blue-700 hover:bg-blue-50 font-bold text-lg px-10 py-4 rounded-2xl shadow-lg transition-all"
              >
                <Rotate3d className="w-5 h-5 mr-2" />
                Start
              </button>
              <p className="text-blue-100 text-sm mt-4 max-w-sm mx-auto leading-relaxed">
                Could the right shape be turned to match the left, or would you
                have to flip it? Those two are the same shape. Four practice
                rounds, then {SCORED_TOTAL} that count. Keys S and M work too.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 mt-10 max-w-md mx-auto">
              {[
                { same: true, label: 'Same', key: 's', colour: 'bg-emerald-400' },
                { same: false, label: 'Mirror', key: 'm', colour: 'bg-rose-400' },
              ].map((b) => (
                <button
                  key={b.label}
                  type="button"
                  onPointerDown={(e) => { e.preventDefault(); respond(b.same) }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); respond(b.same) } }}
                  className={`relative h-16 rounded-xl font-bold text-lg text-gray-900 shadow-md active:scale-95 transition-transform touch-manipulation ${b.colour}`}
                >
                  {b.label}
                  <span className="hidden sm:block absolute top-1 right-2 text-[10px] font-semibold text-gray-900/50 uppercase">
                    {b.key}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : result.usable ? (
        <>
          <div className={`bg-gradient-to-br ${tier ? tier.ring : 'from-cyan-500 to-blue-600'} px-6 sm:px-8 py-10 text-center`}>
            {isNewBest && (
              <span className="inline-block px-3 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-xs font-bold tracking-wide mb-4">
                NEW PERSONAL BEST
              </span>
            )}
            <p className="text-xs font-semibold text-white/70 uppercase tracking-[0.2em] mb-2">
              {hasRate ? 'Rotation speed' : 'Correct answers'}
            </p>
            <p className="text-8xl sm:text-9xl font-bold text-white tabular-nums leading-none drop-shadow-sm">
              {countUp}
              <span className="text-3xl sm:text-4xl font-bold text-white/80 ml-2">{hasRate ? '°/s' : '%'}</span>
            </p>
            <p className="text-3xl sm:text-4xl font-bold text-white mt-4 tracking-tight">
              {hasRate ? tier.label : 'Too quick to measure'}
            </p>
            <p className="text-white/80 mt-1">
              {hasRate ? tier.sub : 'Your times barely changed with the angle, so there was no slope to measure'}
            </p>
            <p className="text-white/80 text-xs mt-3 tabular-nums">
              {result.correct}/{result.total} correct
              {hasRate && ' · the original 1971 study measured about 60°/s for 3D figures'}
            </p>

            {/* Time per angle. The rising bars are the effect itself. */}
            <div className="mt-6 mx-auto max-w-xs">
              <div className="flex items-end justify-between gap-2 h-24">
                {result.byAngle.map((b) => (
                  <div key={b.angle} className="flex-1 flex flex-col items-center justify-end h-full">
                    <span className="text-[10px] text-white/80 tabular-nums mb-1">
                      {b.ms !== null ? `${(b.ms / 1000).toFixed(1)}s` : ''}
                    </span>
                    <div
                      className="w-full rounded-t-md bg-white/80"
                      style={{ height: b.ms !== null && slowest ? `${Math.max(8, (b.ms / slowest) * 100)}%` : '4%' }}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-between gap-2 mt-1">
                {result.byAngle.map((b) => (
                  <span key={b.angle} className="flex-1 text-center text-[10px] text-white/70 tabular-nums">{b.angle}°</span>
                ))}
              </div>
              <p className="text-[11px] text-white/70 mt-2">Your average answer time at each angle</p>
            </div>
          </div>

          <ResultFooter onRestart={start} best={best} />
        </>
      ) : (
        <>
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 px-6 sm:px-8 py-10 text-center">
            <p className="text-xs font-semibold text-white/70 uppercase tracking-[0.2em] mb-2">Correct answers</p>
            <p className="text-8xl sm:text-9xl font-bold text-white tabular-nums leading-none drop-shadow-sm">
              {result.correct}<span className="text-4xl text-white/80">/{result.total}</span>
            </p>
            <p className="text-3xl sm:text-4xl font-bold text-white mt-4 tracking-tight">Too many misses to score</p>
            <p className="text-white/85 mt-2 max-w-sm mx-auto leading-relaxed">
              Guessing gets half of these right, so a speed only means something
              above {Math.round(MIN_ACCURACY * 100)}% correct. Take your time: the
              score comes from how your time changes with the angle, not from
              going fast.
            </p>
          </div>

          <ResultFooter onRestart={start} best={best} />
        </>
      )}

      {phase === 'finished' && (
        <TestPromptPopup
          delayMs={4000}
          storageKey="biqt_rotation_prompt"
          headline="Spatial skill measured. Now the rest."
          body={
            <>
              You just measured mental rotation, one part of spatial reasoning.
              Our 50-question assessment covers spatial, verbal and numerical
              reasoning together, and gives you a full-scale score with your
              exact percentile.
            </>
          }
        />
      )}
    </div>
  )
}

function ResultFooter({ onRestart, best }) {
  return (
    <div className="p-6 sm:p-8">
      <Link
        href="/test"
        className="group relative block w-full overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-[length:200%_100%] hover:bg-[position:100%_0] p-[3px] shadow-xl shadow-purple-500/25 transition-[background-position] duration-700"
      >
        <span className="block rounded-[13px] bg-white px-6 py-5 text-center transition-colors group-hover:bg-transparent">
          <span className="block text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent group-hover:text-white transition-colors">
            Now find your actual IQ
          </span>
          <span className="mt-1 flex items-center justify-center text-sm font-medium text-gray-500 group-hover:text-blue-100 transition-colors">
            50 questions · all four domains · instant score
            <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
          </span>
        </span>
      </Link>

      <p className="text-sm text-gray-600 text-center leading-relaxed mt-5">
        Mental rotation is one component of spatial reasoning. It is a real part
        of what IQ tests measure, and a small one: a full score also draws on
        verbal, numerical and memory abilities.
      </p>

      <div className="flex items-center justify-center gap-5 mt-5">
        <button
          type="button"
          onClick={onRestart}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 font-semibold text-sm transition-colors"
        >
          <RotateCcw className="w-4 h-4 mr-1.5" />
          Play again
        </button>
        {best !== null && (
          <span className="text-sm text-gray-400 tabular-nums">Best: {best}°/s</span>
        )}
      </div>

      <p className="text-xs text-gray-400 mt-5 text-center leading-relaxed">
        There is no normative sample for this format, so there is no percentile
        here. The labels describe your speed rather than rank it. Flat shapes are
        simpler to turn than the 3D figures in the original study, so expect a
        higher number than 60°/s.
      </p>
    </div>
  )
}
